/* eslint-disable no-console */
/*
 * Shared photo lightbox (Bootstrap Modal), used by the Design Gallery and My Account
 * favorites. Shell markup is Views/Shared/_PhotoLightboxModal.cshtml, styling is
 * assets/src/scss/modules/_photo-lightbox.scss.
 */
import { triggerCustomGAEvent } from '../utils/data-layer-utils';

const swipeThreshold = 40;
const bodyOpenClass = 'photo-lightbox-open';

const panelMinWidth = 320; // keeps the caption readable on tall, narrow photos
const panelMaxWidth = 900; // matches $photo-lightbox-width
const panelViewportRatio = 0.7; // matches the image's max-height: 70vh
const panelViewportGutter = 32;
const panelResizeDelay = 100;
const panelToolbarHeightSlack = 150; // the largest innerHeight jump a browser toolbar accounts for

// Only touch browsers collapse and restore chrome while you scroll. Desktop never does, so it
// keeps recomputing on every resize exactly as before.
const hasCollapsingToolbars = typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;

// Long enough to read "Link copied", short enough that it is gone before the next share.
const shareStatusClearDelay = 3000;

// bootstrap.bundle.min.js is loaded `async`, so it may not have run yet when a hash deep
// link asks us to open during init. Deferring rather than skipping, since the usual
// `if (window.bootstrap)` guard drops the deep link on a cold cache. The poll covers the
// script having loaded before the listener was attached.
const whenBootstrapReady = (callback) => {
  const isReady = () => Boolean(window.bootstrap && window.bootstrap.Modal);

  if (isReady()) {
    callback();
    return;
  }

  let pollId = 0;
  let done = false;

  const run = () => {
    if (done || !isReady()) {
      return;
    }
    done = true;
    window.clearInterval(pollId);
    callback();
  };

  pollId = window.setInterval(run, 50);
  window.setTimeout(() => { window.clearInterval(pollId); }, 10000);

  const script = document.querySelector('script[src*="bootstrap.bundle"]');
  if (script) {
    script.addEventListener('load', run, { once: true });
  }
};

const createPhotoLightbox = (options) => {
  const {
    modalId,
    container,
    itemSelector,
    getItems,
    updateHash = false,
    requireAuthToAdd = false,
    onSectionSubmit = null,

    shareBaseUrl = '',
    shareTitle = document.title,
  } = options;

  const noop = () => {};
  const modalEl = document.getElementById(modalId);

  if (!modalEl) {
    return {
      open: noop, close: noop, refresh: noop, destroy: noop,
    };
  }

  const dialogEl = modalEl.querySelector('.photo-lightbox-dialog');
  const imageEl = modalEl.querySelector('.photo-lightbox-image');
  const stageEl = modalEl.querySelector('.photo-lightbox-stage');
  const actionsEl = modalEl.querySelector('.photo-lightbox-actions');
  const productEl = modalEl.querySelector('.photo-lightbox-product');
  const productListEl = modalEl.querySelector('.photo-lightbox-product-list');
  const controlEl = modalEl.querySelector('.photo-lightbox-control');
  const colorEl = modalEl.querySelector('.photo-lightbox-color');
  const creditEl = modalEl.querySelector('.photo-lightbox-credit');
  const colorListEl = modalEl.querySelector('.photo-lightbox-color-list');
  const controlLinkEl = controlEl ? controlEl.querySelector('.photo-lightbox-row-link') : null;
  const shareEl = modalEl.querySelector('.photo-lightbox-share-toggle');
  const shareStatusEl = modalEl.querySelector('.photo-lightbox-share-status');
  const navButtons = Array.from(modalEl.querySelectorAll('[data-lightbox-nav]'));

  const readyFullImages = new Set();
  let modalInstance = null;
  let currentItem = null;
  let currentIndex = -1;
  let previousFocus = null;
  let pendingFavoriteForm = null;
  let pendingOpenIndex = -1;
  let awaitingBootstrap = false;
  let suppressFocusRestore = false;
  let touchStartX = 0;
  let panelResizeTimer = 0;
  let shareStatusTimer = 0;
  // The viewport the current panel geometry was measured against, for the resize guard below.
  let lastPanelViewportWidth = 0;
  let lastPanelViewportHeight = 0;

  const resolveContainer = () => (
    typeof container === 'string' ? document.querySelector(container) : container
  );

  const resolveItems = () => {
    if (typeof getItems === 'function') {
      return getItems();
    }
    const containerEl = resolveContainer();
    return containerEl ? Array.from(containerEl.querySelectorAll(itemSelector)) : [];
  };

  const getAntiForgeryToken = () => {
    const field = document.querySelector('input[name="__RequestVerificationToken"]');
    return field ? field.value : '';
  };

  // Width for the photo to fill the panel edge to edge, clamped so a panorama cannot outgrow
  // the viewport and a tall photo cannot squeeze the caption.
  const applyPanelRatio = (ratio) => {
    if (!dialogEl || !ratio || !Number.isFinite(ratio)) {
      return;
    }
    const maxWidth = Math.min(panelMaxWidth, window.innerWidth - panelViewportGutter);
    const heightBudget = window.innerHeight * panelViewportRatio;
    // floor, not round: rounding up letterboxes by a sub-pixel and shows the white stage
    // through as a hairline.
    const width = Math.floor(Math.min(Math.max(heightBudget * ratio, panelMinWidth), maxWidth));
    dialogEl.style.setProperty('--photo-lightbox-panel-width', `${width}px`);
    dialogEl.style.setProperty('--photo-lightbox-panel-max-height', `${Math.ceil(width / ratio)}px`);

    lastPanelViewportWidth = window.innerWidth;
    lastPanelViewportHeight = window.innerHeight;
  };

  // Re-read on every load, in case a thumbnail and its full asset disagree on ratio.
  const applyPanelSize = () => {
    if (imageEl && imageEl.naturalWidth && imageEl.naturalHeight) {
      applyPanelRatio(imageEl.naturalWidth / imageEl.naturalHeight);
    }
  };

  const getThumbnailSrc = (item) => {
    const img = item.querySelector('img');
    if (!img) {
      return '';
    }
    // Gallery tiles are lazyloaded, so before Lazyload() runs the thumbnail url
    // is still only on the img's own data-src.
    return img.currentSrc || img.getAttribute('src') || img.getAttribute('data-src') || '';
  };

  // Open on the thumbnail and upgrade to the full asset once it has decoded, so navigation
  // never shows an empty frame.
  const preloadFullImage = (src) => {
    if (!src) {
      return Promise.reject(new Error('No image source to preload'));
    }
    if (readyFullImages.has(src)) {
      return Promise.resolve();
    }

    const preload = new Image();
    preload.decoding = 'async';
    preload.src = src;

    const loaded = typeof preload.decode === 'function'
      ? preload.decode()
      : new Promise((resolve, reject) => {
        preload.addEventListener('load', resolve, { once: true });
        preload.addEventListener('error', reject, { once: true });
      });

    return loaded.then(() => {
      readyFullImages.add(src);
    });
  };

  // Hides the whole row, label included, when the value is absent, and falls back to plain
  // text when there is no page to point at, so nothing becomes a link to nowhere.
  const renderRow = (rowEl, linkEl, value, url) => {
    if (!rowEl) {
      return;
    }

    rowEl.classList.toggle('d-none', value.length === 0);
    if (value.length === 0 || !linkEl) {
      return;
    }

    linkEl.textContent = value;
    linkEl.classList.toggle('link', Boolean(url));
    linkEl.classList.toggle('photo-lightbox-row-link-plain', !url);
    if (url) {
      linkEl.setAttribute('href', url);
    } else {
      linkEl.removeAttribute('href');
    }
  };

  // Product and Color are both multi-valued, so both render through here. Built as DOM nodes
  // rather than an innerHTML string, since the values are Widen-authored.
  const renderLinkListRow = (rowEl, listEl, entries, separator) => {
    if (!rowEl) {
      return;
    }

    rowEl.classList.toggle('d-none', entries.length === 0);
    if (entries.length === 0 || !listEl) {
      return;
    }

    listEl.textContent = '';

    entries.forEach((entry, index) => {
      if (index > 0) {
        listEl.appendChild(document.createTextNode(separator));
      }

      const linkEl = document.createElement(entry.url ? 'a' : 'span');
      linkEl.className = entry.url
        ? 'link photo-lightbox-row-link'
        : 'photo-lightbox-row-link photo-lightbox-row-link-plain';

      if (entry.url) {
        linkEl.setAttribute('href', entry.url);
        linkEl.setAttribute('target', '_blank');
        linkEl.setAttribute('rel', 'noopener');
      }

      linkEl.textContent = entry.name;
      listEl.appendChild(linkEl);
    });
  };

  const splitList = value => String(value || '').split('|').map(part => part.trim());

  const readRow = (item, attribute) => (item.getAttribute(attribute) || '').trim();

  // Paired up before filtering, so a stray empty name cannot shift the urls out of step.
  const readPairedList = (item, nameAttribute, urlAttribute) => {
    const names = splitList(item.getAttribute(nameAttribute));
    const urls = splitList(item.getAttribute(urlAttribute));
    const entries = [];

    names.forEach((name, index) => {
      if (name.length > 0) {
        entries.push({ name, url: urls[index] || '' });
      }
    });

    return entries;
  };

  const renderCaption = (item) => {
    renderLinkListRow(
      productEl,
      productListEl,
      readPairedList(item, 'data-product-names', 'data-product-urls'),
      ', ',
    );

    renderRow(
      controlEl,
      controlLinkEl,
      readRow(item, 'data-control-type'),
      readRow(item, 'data-control-type-url'),
    );

    renderLinkListRow(
      colorEl,
      colorListEl,
      readPairedList(item, 'data-color-labels', 'data-color-urls'),
      ' / ',
    );

    if (creditEl) {
      const credit = readRow(item, 'data-photo-credit');
      creditEl.textContent = credit.length === 0 ? '' : `${credit}`;
      creditEl.classList.toggle('d-none', credit.length === 0);
    }
  };

  const renderActions = (item) => {
    if (!actionsEl) {
      return;
    }
    const slot = item.querySelector('.js-lightbox-actions');
    actionsEl.innerHTML = slot ? slot.innerHTML : '';
    actionsEl.classList.toggle('d-none', actionsEl.innerHTML.trim().length === 0);
  };

  const getPhotoId = item => (item ? item.getAttribute('data-widen-id') || '' : '').trim();

  const buildShareUrl = (item) => {
    const photoId = getPhotoId(item);
    if (!photoId) {
      return '';
    }

    const shareUrl = new URL(shareBaseUrl || window.location.pathname, window.location.origin);
    shareUrl.searchParams.set('photo', photoId);
    return shareUrl.href;
  };

  const setShareStatus = (message) => {
    if (!shareStatusEl) {
      return;
    }

    window.clearTimeout(shareStatusTimer);
    shareStatusEl.textContent = message;
    shareStatusEl.classList.toggle('d-none', message.length === 0);

    if (message) {
      shareStatusTimer = window.setTimeout(() => { setShareStatus(''); }, shareStatusClearDelay);
    }
  };

  const legacyCopyShareUrl = (url, restoreFocus) => {
    const field = document.createElement('textarea');
    field.value = url;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;top:-1000px;left:0;opacity:0;';
    modalEl.appendChild(field);

    let copied = false;
    try {
      field.select();
      copied = document.execCommand('copy');
    } catch (error) {
      console.error(error);
    }

    modalEl.removeChild(field);

    if (restoreFocus && shareEl) {
      shareEl.focus();
    }

    return copied;
  };

  // The Clipboard API is gated on a secure context
  const copyShareUrl = (url, restoreFocus) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(url);
    }

    return legacyCopyShareUrl(url, restoreFocus)
      ? Promise.resolve()
      : Promise.reject(new Error('Copy unavailable'));
  };

  // fromKeyboard decides whether the legacy copy path puts focus back on the button. Only
  // the clipboard fallback moves focus at all, so it is the only thing that needs to know.
  const shareCurrentPhoto = (fromKeyboard) => {
    const url = buildShareUrl(currentItem);
    if (!url) {
      return;
    }

    const photoId = getPhotoId(currentItem);

    const report = (method) => {
      try {
        triggerCustomGAEvent({
          action: 'share',
          category: 'Photo Lightbox',
          label: method,
          value: photoId,
        });
      } catch (error) {
        console.error(error);
      }
    };

    const copyFallback = () => copyShareUrl(url, fromKeyboard)
      .then(() => {
        setShareStatus('Link copied');
        report('copy');
      })
      .catch(() => {
        setShareStatus('Copy failed');
      });

    // navigator.share has to be reached in the same task as the click. Nothing may be awaited
    // above this line or the user gesture is spent and it rejects with NotAllowedError.
    if (navigator.share) {
      navigator.share({ title: shareTitle, url }).then(
        () => { report('native'); },
        (error) => {
          if (error && error.name === 'AbortError') {
            return;
          }
          copyFallback();
        },
      );
      return;
    }

    copyFallback();
  };

  const setHash = (item) => {
    if (!updateHash) {
      return;
    }
    const widenId = getPhotoId(item);
    if (!widenId) {
      return;
    }
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${widenId}`);
  };

  const clearHash = () => {
    if (!updateHash) {
      return;
    }
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  };

  const showPhoto = (index) => {
    const items = resolveItems();
    const item = items[index];
    if (!item || !imageEl) {
      return false;
    }

    currentIndex = index;
    currentItem = item;

    const fullSrc = item.getAttribute('data-src') || '';
    const description = item.getAttribute('data-description') || '';

    imageEl.setAttribute('alt', description);

    const tileImg = item.querySelector('img');
    if (tileImg && tileImg.complete && tileImg.naturalWidth) {
      applyPanelRatio(tileImg.naturalWidth / tileImg.naturalHeight);
    }

    imageEl.src = readyFullImages.has(fullSrc)
      ? fullSrc
      : (getThumbnailSrc(item) || fullSrc);

    renderCaption(item);
    renderActions(item);
    setHash(item);

    if (shareEl) {
      shareEl.classList.toggle('d-none', buildShareUrl(item).length === 0);
    }
    setShareStatus('');

    const hasSiblings = items.length > 1;
    navButtons.forEach((button) => {
      button.classList.toggle('d-none', !hasSiblings);
    });

    if (!readyFullImages.has(fullSrc)) {
      preloadFullImage(fullSrc).then(() => {
        if (currentItem === item) {
          imageEl.src = fullSrc;
        }
      }).catch(() => {});
    }

    return true;
  };

  const showAdjacent = (direction) => {
    const items = resolveItems();
    if (items.length < 2 || currentIndex < 0) {
      return;
    }

    const nextIndex = (currentIndex + direction + items.length) % items.length;
    showPhoto(nextIndex);

    const aheadIndex = (nextIndex + direction + items.length) % items.length;
    const ahead = items[aheadIndex];
    if (ahead) {
      preloadFullImage(ahead.getAttribute('data-src') || '').catch(() => {});
    }
  };

  const getModal = () => {
    if (!modalInstance) {
      modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalEl);
    }
    return modalInstance;
  };

  const close = () => {
    if (modalInstance) {
      modalInstance.hide();
    }
  };

  const open = (index, trigger) => {
    previousFocus = trigger || document.activeElement;

    if (window.bootstrap && window.bootstrap.Modal) {
      if (showPhoto(index)) {
        getModal().show();
      }
      return;
    }

    pendingOpenIndex = index;
    if (awaitingBootstrap) {
      return;
    }
    awaitingBootstrap = true;
    whenBootstrapReady(() => {
      awaitingBootstrap = false;
      const queued = pendingOpenIndex;
      pendingOpenIndex = -1;
      if (queued >= 0 && showPhoto(queued)) {
        getModal().show();
      }
    });
  };

  const refresh = () => {
    if (!currentItem) {
      return;
    }
    currentIndex = resolveItems().indexOf(currentItem);
    if (currentIndex === -1) {
      close();
    }
  };

  const destroy = () => {
    close();
    if (modalInstance) {
      modalInstance.dispose();
      modalInstance = null;
    }
  };

  const postFavoriteForm = (form) => {
    const mode = form.getAttribute('data-response-mode') || 'button';
    if (mode === 'section') {
      const photoId = currentItem ? currentItem.getAttribute('data-photo-id') : '';
      close();
      if (typeof onSectionSubmit === 'function') {
        onSectionSubmit(form, { photoId });
      }
      return Promise.resolve();
    }

    const sourceSlot = currentItem ? currentItem.querySelector('.js-lightbox-actions') : null;
    const buttons = Array.from(form.querySelectorAll('button'));
    buttons.forEach((button) => { button.disabled = true; });

    const body = new URLSearchParams(new FormData(form));
    const token = getAntiForgeryToken();
    if (token && !body.has('__RequestVerificationToken')) {
      body.append('__RequestVerificationToken', token);
    }

    return fetch(form.getAttribute('action'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body,
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Favorite request failed with status ${response.status}`);
      }
      return response.text();
    }).then((html) => {
      if (actionsEl) {
        actionsEl.innerHTML = html;
      }
      if (sourceSlot) {
        sourceSlot.innerHTML = html;
      }
    }).catch((error) => {
      console.error(error);
      buttons.forEach((button) => { button.disabled = false; });
    });
  };

  const submitPendingFavorite = () => {
    if (!pendingFavoriteForm) {
      return;
    }
    const form = pendingFavoriteForm;
    pendingFavoriteForm = null;
    postFavoriteForm(form);
  };

  const launchAuthModal = () => {
    if (!window.Login || typeof window.Login.launchLoginModal !== 'function') {
      pendingFavoriteForm = null;
      return;
    }

    const reopenIndex = currentIndex;
    const reopenTrigger = previousFocus;
    suppressFocusRestore = true;
    close();

    const callbacks = {
      onLoginSuccess: () => {
        window.isAuthenticated = true;
        if (reopenIndex >= 0) {
          open(reopenIndex, reopenTrigger);
        }
        submitPendingFavorite();
      },
      onLoginFail: () => {
        pendingFavoriteForm = null;
      },
    };
    window.onSubmitCallbacks = callbacks;
    window.Login.launchLoginModal({ type: 'photo', callbacks });
  };

  // ----- Wiring -----

  // Delegated on document, since My Account replaces the favorites section's innerHTML on
  // remove and would discard a container-bound listener.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(itemSelector);
    if (!trigger || event.target.closest('.js-lightbox-actions, a')) {
      return;
    }
    const containerEl = resolveContainer();
    if (!containerEl || !containerEl.contains(trigger)) {
      return;
    }
    const index = resolveItems().indexOf(trigger);
    if (index === -1) {
      return;
    }
    event.preventDefault();
    // Focus returns to whatever the user actually activated, which on a tile is
    // its button rather than the tile wrapper.
    open(index, event.target.closest('button') || trigger);
  });

  navButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      showAdjacent(button.getAttribute('data-lightbox-nav') === 'prev' ? -1 : 1);
    });
  });

  if (shareEl) {
    shareEl.addEventListener('click', (event) => {
      event.preventDefault();
      shareCurrentPhoto(event.detail === 0);
    });
  }

  modalEl.addEventListener('submit', (event) => {
    const form = event.target.closest('.js-lightbox-favorite-form');
    if (!form) {
      return;
    }
    event.preventDefault();

    const isAdd = form.getAttribute('data-favorite-action') === 'add';
    if (requireAuthToAdd && isAdd && !window.isAuthenticated) {
      pendingFavoriteForm = form;
      launchAuthModal();
      return;
    }
    postFavoriteForm(form);
  });

  // Bootstrap appends .modal-backdrop to <body>, so the backdrop cannot be
  // scoped under the modal. A body class is the only way to reach it.
  modalEl.addEventListener('show.bs.modal', () => {
    document.body.classList.add(bodyOpenClass);
  });

  modalEl.addEventListener('hidden.bs.modal', () => {
    document.body.classList.remove(bodyOpenClass);
    clearHash();
    setShareStatus('');
    currentItem = null;
    currentIndex = -1;
    // Bootstrap only restores focus for modals opened through
    // data-bs-toggle="modal"; a programmatic show() strands focus on <body>.
    if (!suppressFocusRestore && previousFocus && typeof previousFocus.focus === 'function') {
      previousFocus.focus();
    }
    suppressFocusRestore = false;
    previousFocus = null;
  });

  document.addEventListener('keydown', (event) => {
    if (!modalEl.classList.contains('show')) {
      return;
    }
    if (event.target.closest && event.target.closest('input, textarea, select')) {
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showAdjacent(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      showAdjacent(1);
    }
  });

  // Fires for both the thumbnail and the full-asset upgrade. Both resolve to the
  // same panel width because the ratio is unchanged, so the upgrade is seamless.
  if (imageEl) {
    imageEl.addEventListener('load', applyPanelSize);
  }

  // The clamps are viewport-relative, so the panel has to be recomputed on resize.
  window.addEventListener('resize', () => {
    if (!modalEl.classList.contains('show')) {
      return;
    }
    // Mobile browsers collapse and restore their toolbars as you scroll, which moves
    // innerHeight by ~100-150px and fires resize each time. Acting on that resizes the panel
    // mid-scroll. Rotation and window drags move the width as well, so a height-only change
    // this small is the toolbar and nothing else.
    const heightDelta = Math.abs(window.innerHeight - lastPanelViewportHeight);
    if (hasCollapsingToolbars
      && window.innerWidth === lastPanelViewportWidth
      && heightDelta <= panelToolbarHeightSlack) {
      return;
    }
    window.clearTimeout(panelResizeTimer);
    panelResizeTimer = window.setTimeout(applyPanelSize, panelResizeDelay);
  });

  if (stageEl) {
    stageEl.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0].clientX;
    }, { passive: true });

    stageEl.addEventListener('touchend', (event) => {
      const delta = event.changedTouches[0].clientX - touchStartX;
      if (Math.abs(delta) < swipeThreshold) {
        return;
      }
      showAdjacent(delta < 0 ? 1 : -1);
    }, { passive: true });
  }

  return {
    open, close, refresh, destroy,
  };
};

export default createPhotoLightbox;
/* eslint-enable no-console */
